import { useContext, useEffect, useRef, useState } from "react";
import { track } from "../track";
import { isKeychain, type LeadKind, type PreviewPiece } from "../types";
import type { Words } from "./copy";
import OrderBar, { type SizeAsk } from "./OrderBar";
import { FromPrice, Ladder, sizesPickable } from "./Facts";
import { MAX_UNITS } from "./multiBuy";
import { QtyStepper } from "./Quantity";
import { BasketContext, pieceKey, qtySel } from "./selection";
import { PageContext } from "./page";
import { KIND_KEY, openingSize } from "./kinds";
import ChristmasBase from "./ChristmasBase";
import { baseKindOf } from "./christmas";

/** What one piece costs and how to order it, under the drawing of it.
 *
 *  Only on a page of several. Pieces are independent, and they can have as many
 *  of them as they like: two people can be shown as each of them on their own
 *  and the two together, which is three products at three prices. Nothing here
 *  adds anything up, so each one carries its own way out to the shop, and
 *  "Order them together" under the cards is for wanting more than one.
 *
 *  Named as well as priced, because two of the three can be the same product at
 *  the same price and only the name and their own photo tell them apart. The
 *  price is where it starts, with every size under it, as on a page of one. */
export default function PieceOffer({
  piece,
  fallbackKind,
  drawingUrl,
  words: t,
  autoOpen,
  othersOffered,
}: {
  piece: PreviewPiece;
  /** The lead's product, for a piece written before pieces carried their own. */
  fallbackKind: LeadKind;
  /** This piece's drawing, for the order sheet's first step. */
  drawingUrl?: string | null;
  words: Words;
  /** This is the piece the order mail's "Pay on my page" was sent for: its
   *  sheet opens by itself, as the bar's does on a page of one. */
  autoOpen?: boolean;
  /** Another piece on this page has a price and a listing. Only then does
   *  a piece without one say so: where none has, the page is not priced yet
   *  and a line on every card would read as broken rather than early. */
  othersOffered?: boolean;
}) {
  const page = useContext(PageContext);
  const info = useContext(BasketContext);
  // A size tapped in the ladder, for this card's own sheet.
  const [sizeAsk, setSizeAsk] = useState<SizeAsk | null>(null);
  const kind = piece.kind ?? fallbackKind;
  const sizes = isKeychain(kind) ? t.t("sizesKeychain") : t.t("sizes");
  // The order mail's "Pay on my page" for this piece, on a page that lets them
  // choose: the piece goes into the order at its opening size and the sheet
  // opens on it, as its own sheet used to.
  const linked = useRef(false);
  useEffect(() => {
    if (!autoOpen || linked.current || !info?.on || !piece.price?.sizes?.length) return;
    linked.current = true;
    page?.onPick?.(pieceKey("", piece.id), openingSize(piece.price.sizes, kind));
    info.open();
  }, [autoOpen, info?.on]);
  // On a page that lets them choose (selection.ts), this card adds its piece
  // to the order rather than opening a checkout of its own: the size is a
  // tap on the ladder, "Add to order" puts it in, and the bar at the bottom
  // says what the order comes to. A piece that cannot be paid for here (no
  // sizes) keeps its own way out to the shop, below.
  const priced = piece.price;
  if (info?.on && priced?.sizes?.length) {
    const key = pieceKey("", piece.id);
    const line = info.basket.lines.find((l) => l.key === key) ?? null;
    const done = !!page?.ordered && !page.admin;
    const sizesOf = priced.sizes;
    // One payment takes MAX_UNITS pieces in all; a card not in the order yet
    // cannot be added past that, by its button or by a tap on the ladder.
    const full = !line && info.basket.units >= MAX_UNITS;
    const choose = (size: string | null) => {
      page?.onPick?.(key, size);
      if (!size) page?.onQty?.(qtySel(key), 1);
      track(size ? "piece.selected" : "piece.removed", { piece: piece.id, ...(size ? { size } : {}) });
    };
    return (
      <div className={line ? "offer offer-in" : "offer"}>
        <div>
          <strong className="small">{t.t(KIND_KEY[kind] ?? "kindMinime")}</strong>
          <div>
            <FromPrice price={priced} words={t} />
          </div>
          {priced.vatNote && (
            <p className="tiny muted" style={{ margin: "2px 0 0" }}>
              {priced.vatNote}
            </p>
          )}
          <Ladder
            price={priced}
            code={page?.promoCode ?? ""}
            kind={kind}
            picked={line?.size ?? null}
            words={t}
            onPick={done || full ? undefined : (size) => choose(size)}
          />
        </div>
        {!done &&
          (line ? (
            <div className="stack">
              <div className="row-between">
                <span className="small offer-in-tag">✓ {t.t("basketIn")}</span>
                {/* A word, not a second pill: beside the stepper under it, two
                    outlined capsules of different widths read as a pair of
                    equal controls stacked out of line. */}
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => choose(null)}>
                  {t.t("basketRemove")}
                </button>
              </div>
              {info.basket.owns && (
                <div className="qty-row qty-row-tight">
                  <span className="tiny muted">{t.t("multiBuyQty")}</span>
                  <QtyStepper
                    value={line.qty}
                    max={line.qty + Math.max(0, MAX_UNITS - info.basket.units)}
                    onChange={(n) => page?.onQty?.(qtySel(key), n)}
                    words={t}
                    label={`${t.t("multiBuyQty")} ${t.t(KIND_KEY[kind] ?? "kindMinime")}`}
                  />
                </div>
              )}
              {/* Its Christmas base, once it is in the order, for a figure
                  where the page offers one: kept with its "how many", so the
                  summary, the bar and "Order them together" carry it. */}
              {page?.baseLimits && (
                <ChristmasBase
                  compact
                  kind={baseKindOf(kind, piece.price) ?? kind}
                  size={line.size}
                  sizes={sizesOf.map((s) => s.size)}
                  choice={page.bases?.[qtySel(key)]}
                  onChange={(next) => page.onBase?.(qtySel(key), next)}
                  onSize={(size) => choose(size)}
                  drawingUrl={drawingUrl ?? piece.photoUrl ?? null}
                  words={t}
                />
              )}
            </div>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-soft btn-block"
                disabled={full}
                onClick={() => choose(openingSize(sizesOf, kind))}
              >
                {t.t("basketAdd")}
              </button>
              {full && <p className="tiny muted" style={{ margin: "4px 0 0" }}>{t.t("qtyLimit", { n: String(MAX_UNITS) })}</p>}
            </>
          ))}
      </div>
    );
  }

  // Nothing to offer without a price: no empty line where a number should be,
  // but a word that says so, rather than a drawing with no way on and no
  // reason why.
  if (!piece.price || !piece.etsyUrl) {
    if (!othersOffered) return null;
    return (
      <p className="tiny muted" style={{ margin: "8px 0 0" }}>
        {t.t("pieceNoOffer")}
      </p>
    );
  }

  return (
    <div className="offer">
      {/* A plain block, not a row: inside a flex row the ladder shrank to the
          width of its words and the sizes stood as narrow pills on the left,
          where a page of one draws them across the card. */}
      <div>
        <strong className="small">{t.t(KIND_KEY[kind] ?? "kindMinime")}</strong>
        <div>
          <FromPrice price={piece.price} words={t} />
        </div>
        {piece.price.vatNote && (
          <p className="tiny muted" style={{ margin: "2px 0 0" }}>
            {piece.price.vatNote}
          </p>
        )}
        {piece.price.sizes?.length ? (
          <Ladder
            price={piece.price}
            code={page?.promoCode ?? ""}
            kind={kind}
            picked={sizeAsk?.size ?? null}
            words={t}
            onPick={
              sizesPickable(page)
                ? (size) => setSizeAsk((ask) => ({ size, n: (ask?.n ?? 0) + 1 }))
                : undefined
            }
          />
        ) : (
          <p className="tiny muted" style={{ margin: "2px 0 0" }}>
            {sizes}
          </p>
        )}
      </div>
      <OrderBar
        words={t}
        from="piece"
        offer={piece.price}
        etsyUrl={piece.etsyUrl}
        pieceKind={kind}
        pieceId={piece.id}
        drawingUrl={drawingUrl ?? piece.photoUrl ?? null}
        autoOpen={autoOpen}
        sizeAsk={sizeAsk}
      />
    </div>
  );
}
